'use client';

import { useCallback, useMemo, useState } from 'react';
import { useAuthService } from './useAuthService';
import { AuthErrorCode, UserType } from '../../constants/auth';
import type { ApiErrorResponse } from '../../infrastructure/auth/utils/types';
import { formatE164PhoneNumber } from '../../utils/phone';

type UseOTPActionsParams = {
  phoneNumber?: string;
  countryCode?: string;
};

export type OTPVerificationResult =
  | { isNewUser: true }
  | { isNewUser: false; otpVerified: boolean; candidateEmails?: string[] };

type UseOTPActionsReturn = {
  otpCode: string | undefined;
  verificationResult: OTPVerificationResult | undefined;
  errorCode: AuthErrorCode | undefined;
  isSending: boolean;
  isVerifying: boolean;
  sendOtp: () => Promise<boolean>;
  resendOtp: () => Promise<boolean>;
  verifyOtp: (code: string) => Promise<OTPVerificationResult | null>;
  clearError: () => void;
};

/**
 * Pure OTP operations hook (platform-agnostic)
 *
 * Handles OTP send/verify without step management.
 * Designed for reuse across web and mobile platforms.
 *
 * @param phoneNumber - User's phone number
 * @param countryCode - Country code for E.164 formatting
 *
 * @returns OTP state and actions
 *
 * @example
 * const { sendOtp, verifyOtp, verificationResult } = useOTPActions({ phoneNumber, countryCode });
 * await sendOtp();
 * const result = await verifyOtp('123456');
 */
export const useOTPActions = ({ phoneNumber, countryCode }: UseOTPActionsParams = {}): UseOTPActionsReturn => {
  const { useGenerateOtp, useVerifyOtp } = useAuthService();
  const { mutateAsync: generateOtpMutation, isPending: isSending } = useGenerateOtp();
  const { mutateAsync: verifyOtpMutation, isPending: isVerifying } = useVerifyOtp();

  const [otpCode, setOtpCode] = useState<string | undefined>(undefined);
  const [verificationResult, setVerificationResult] = useState<OTPVerificationResult | undefined>(undefined);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | undefined>(undefined);

  const clearError = useCallback(() => {
    setErrorCode(undefined);
  }, []);

  const e164PhoneNumber = useMemo(() => {
    if (!phoneNumber || !countryCode) return undefined;
    return formatE164PhoneNumber({ phoneNumber, countryCode });
  }, [phoneNumber, countryCode]);

  const sendOtp = useCallback(async (): Promise<boolean> => {
    if (!e164PhoneNumber || !countryCode) return false;

    try {
      await generateOtpMutation({ phoneNumber: e164PhoneNumber, countryCode });
      return true;
    } catch (error) {
      const apiError = error as { response?: { data?: ApiErrorResponse } };
      setErrorCode(apiError?.response?.data?.errorCode);
      return false;
    }
  }, [e164PhoneNumber, countryCode, generateOtpMutation]);

  const resendOtp = useCallback(async (): Promise<boolean> => {
    return sendOtp();
  }, [sendOtp]);

  const verifyOtp = useCallback(
    async (code: string): Promise<OTPVerificationResult | null> => {
      if (!e164PhoneNumber || !countryCode) {
        return null;
      }

      setErrorCode(undefined);

      try {
        const result = await verifyOtpMutation({ phoneNumber: e164PhoneNumber, otpCode: code, countryCode });

        if (!result.success || !result.otpVerificationInfo) {
          return null;
        }

        setOtpCode(code);

        const verificationInfo = result.otpVerificationInfo;
        const isGuestUser = verificationInfo?.user?.userType === UserType.Guest;

        // Guest user or no verification info means new user requiring registration
        if (isGuestUser) {
          const newUserResult: OTPVerificationResult = { isNewUser: true };
          setVerificationResult(newUserResult);
          return newUserResult;
        }

        const { user } = verificationInfo;
        const existingUserResult: OTPVerificationResult = {
          isNewUser: false,
          otpVerified: user.otpVerified ?? false,
          candidateEmails: user.candidateEmails,
        };
        setVerificationResult(existingUserResult);
        return existingUserResult;
      } catch (error) {
        const apiError = error as { response?: { data?: ApiErrorResponse } };
        setErrorCode(apiError?.response?.data?.errorCode);
        return null;
      }
    },
    [e164PhoneNumber, countryCode, verifyOtpMutation],
  );

  return {
    otpCode,
    verificationResult,
    errorCode,
    isSending,
    isVerifying,
    sendOtp,
    resendOtp,
    verifyOtp,
    clearError,
  };
};
